import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import { handleAddModifyOperation, fetchFormData } from '../../../Api/ApiMethods';

const EmployeeDataModal = ({ apiEndpoint, isOpen, toggle, mode, selectedEmployee, refreshData }) => {
    const initialEmployeeData = {
        id: '',
        roleId: '', // Tek rol ID'si için roleId kullan
        personId: '',
        firstName: '',
        lastName: '',
        tcId: '',
        job: '',
        department: '',
        phoneNumber: '',
        email: '',
        employmentStartDate: '',
        isBlocked: false
    };

    const [employeeData, setEmployeeData] = useState(initialEmployeeData);
    const [errors, setErrors] = useState({});
    const [roleOptions, setRoleOptions] = useState([]);

    const roleApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/role';

    // Türkçe karakterleri İngilizce karşılıklarına çeviren helper fonksiyon
    const turkishToEnglish = (text) => {
        return text
            .replace(/İ/g, 'I')
            .replace(/ı/g, 'i')
            .replace(/Ğ/g, 'G')
            .replace(/ğ/g, 'g')
            .replace(/Ü/g, 'U')
            .replace(/ü/g, 'u')
            .replace(/Ş/g, 'S')
            .replace(/ş/g, 's')
            .replace(/Ö/g, 'O')
            .replace(/ö/g, 'o')
            .replace(/Ç/g, 'C')
            .replace(/ç/g, 'c')
            .toLowerCase();
    };

    useEffect(() => {
        const loadRoles = async () => {
            if (isOpen && roleApiUrl) {
                try {
                    const response = await fetchFormData({ apiEndpoint: roleApiUrl });
                    
                    let roles = [];
                    // fetchFormData zaten response.data'yı döndürüyor, bu yüzden direkt response.items kullan
                    if (Array.isArray(response?.items)) {
                        roles = response.items;
                        setRoleOptions(roles);
                    } else {
                        setRoleOptions([]);
                    }
                } catch (error) {
                    console.error("Role fetch error:", error);
                    setRoleOptions([]);
                }
            }
        };
        loadRoles();
    }, [isOpen, roleApiUrl]);

    useEffect(() => {
        if (isOpen) {
            if (mode === 'modify' && selectedEmployee) {
                // Kullanıcı verilerinden rol ID'sini al
                // roles objesi {roleId: roleName} formatında
                let currentRoleId = '';
                if (selectedEmployee.roles && typeof selectedEmployee.roles === 'object') {
                    // roles object'inden ilk key'i al (roleId)
                    const roleIds = Object.keys(selectedEmployee.roles);
                    if (roleIds.length > 0) {
                        currentRoleId = String(roleIds[0]);
                    }
                } else if (selectedEmployee.roleId) {
                    currentRoleId = String(selectedEmployee.roleId);
                }

                const formattedStartDate = selectedEmployee.employmentStartDate?.split('T')[0] || '';

                setEmployeeData({
                    ...initialEmployeeData,
                    id: selectedEmployee.id,
                    personId: selectedEmployee.personId,
                    firstName: selectedEmployee.firstName,
                    lastName: selectedEmployee.lastName,
                    tcId: selectedEmployee.tcId,
                    job: selectedEmployee.job,
                    department: selectedEmployee.department,
                    phoneNumber: selectedEmployee.phoneNumber,
                    email: selectedEmployee.email,
                    isBlocked: selectedEmployee.isBlocked,
                    roleId: currentRoleId,
                    employmentStartDate: formattedStartDate
                });
                setErrors({});

            } else if (mode === 'add') {
                resetForm();
            }
        }
    }, [mode, selectedEmployee, isOpen, roleOptions]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        let processedValue = type === 'checkbox' ? checked : value;

        const fieldsToUppercase = ['firstName', 'lastName', 'job', 'department'];

        if (fieldsToUppercase.includes(name) && typeof processedValue === 'string') {
            processedValue = processedValue.toLocaleUpperCase('tr-TR');
        } else if (name === 'email' && typeof processedValue === 'string') {
            // Email için Türkçe karakterleri İngilizce karşılıklarına çevir ve küçült
            processedValue = turkishToEnglish(processedValue);
        } else if (name === 'tcId' && typeof processedValue === 'string') {
            processedValue = processedValue.replace(/[^0-9]/g, '');
            if (processedValue.length > 11) {
                processedValue = processedValue.slice(0, 11);
            }
        } else if (name === 'phoneNumber' && typeof processedValue === 'string') {
            processedValue = processedValue.replace(/[^0-9]/g, '');
            if (processedValue.length > 11) {
                processedValue = processedValue.slice(0, 11);
            }
        }

        setEmployeeData(prevData => ({
            ...prevData,
            [name]: processedValue
        }));

        if (errors[name]) {
            setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
        }
    };

    const handleRoleChange = (e) => {
        const selectedId = e.target.value;
        setEmployeeData(prevData => ({ ...prevData, roleId: selectedId }));

        if (errors.roleId) {
            setErrors(prevErrors => ({ ...prevErrors, roleId: '' }));
        }
    };

    const validateForm = () => {
        const validationErrors = {};
        const identityNumberRegex = /^[0-9]{11}$/;
        const phoneRegex = /^05[0-9]{9}$/;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        // Rol kontrolü sadece modify modunda gerekli
        if (mode === 'modify' && (!employeeData.roleId || employeeData.roleId.trim() === '')) {
            validationErrors.roleId = 'Rol seçimi zorunludur.';
        }

        if (!employeeData.firstName?.trim()) {
            validationErrors.firstName = 'Çalışan adı zorunludur.';
        } else if (!/^[A-ZĞÜŞİÖÇ\s]+$/i.test(employeeData.firstName)) {
            validationErrors.firstName = 'Ad sadece harf ve boşluk içerebilir.';
        }

        if (employeeData.lastName && !/^[A-ZĞÜŞİÖÇ\s]+$/i.test(employeeData.lastName)) {
            validationErrors.lastName = 'Soyad sadece harf ve boşluk içerebilir.';
        }

        if (!employeeData.tcId?.trim()) {
            validationErrors.tcId = 'Kimlik numarası zorunludur.';
        } else if (!identityNumberRegex.test(employeeData.tcId)) {
            validationErrors.tcId = 'TC Kimlik Numarası 11 haneli rakam olmalıdır.';
        }

        if (employeeData.phoneNumber && !phoneRegex.test(employeeData.phoneNumber)) {
            validationErrors.phoneNumber = 'Telefon numarası 05 ile başlamalı ve 11 haneli olmalıdır.';
        }

        // Email kontrolü - add modunda zorunlu
        if (mode === 'add' && !employeeData.email?.trim()) {
            validationErrors.email = 'E-posta adresi zorunludur.';
        } else if (employeeData.email && !emailRegex.test(employeeData.email)) {
            validationErrors.email = 'Geçerli bir e-posta adresi giriniz.';
        }

        if (employeeData.job && employeeData.job.length < 2) {
            validationErrors.job = 'İş alanı en az 2 karakter olmalıdır.';
        }

        if (employeeData.department && employeeData.department.length < 2) {
            validationErrors.department = 'Departman en az 2 karakter olmalıdır.';
        }

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const resetForm = () => {
        setEmployeeData(initialEmployeeData);
        setErrors({});
    };

    const handleCancel = () => {
        resetForm();
        toggle();
    };

    const handleOperation = async () => {
        if (!validateForm()) {
            const firstErrorKey = Object.keys(errors).find(key => errors[key]);
            if (firstErrorKey) {
                const errorElement = document.getElementById(firstErrorKey);
                if (errorElement) errorElement.focus();
            }
            toast.error('Lütfen formdaki hataları düzeltin.');
            return;
        }

        const formattedStartDate = employeeData.employmentStartDate
            ? new Date(employeeData.employmentStartDate).toISOString()
            : null;

        if (mode === 'add') {
            // Yeni kullanıcı eklemek için /api/Account/register endpoint'ini kullan
            const registerData = {
                firstName: employeeData.firstName,
                lastName: employeeData.lastName,
                username: employeeData.email, // Email'i username olarak kullan
                email: employeeData.email,
                password: employeeData.tcId, // Geçici olarak TC kimlik numarasını şifre olarak kullan
                tcId: employeeData.tcId,
                job: employeeData.job,
                department: employeeData.department,
                phoneNumber: employeeData.phoneNumber,
                employmentStartDate: formattedStartDate,
                isBlocked: employeeData.isBlocked
            };

            const registerEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/Account/register';
            
            try {
                await handleAddModifyOperation({
                    apiEndpoint: registerEndpoint,
                    selectedItem: null,
                    mode: 'add',
                    data: registerData,
                    refreshDataCallback: refreshData,
                    componentName: 'Çalışan',
                });
                resetForm();
                toggle();
            } catch (error) {
            }
        } else {
            // Mevcut kullanıcıyı güncellemek için user endpoint'ini kullan
            const roleIdArray = employeeData.roleId ? [employeeData.roleId] : [];
            
            const dataToSend = {
                id: employeeData.id,
                personId: employeeData.personId,
                firstName: employeeData.firstName,
                lastName: employeeData.lastName,
                tcId: employeeData.tcId,
                job: employeeData.job,
                department: employeeData.department,
                phoneNumber: employeeData.phoneNumber,
                email: employeeData.email,
                employmentStartDate: formattedStartDate,
                isBlocked: employeeData.isBlocked,
                roles: roleIdArray
            };

            try {
                await handleAddModifyOperation({
                    apiEndpoint,
                    selectedItem: { id: selectedEmployee?.id },
                    mode: 'modify',
                    data: dataToSend,
                    refreshDataCallback: refreshData,
                    componentName: 'Çalışan',
                    httpMethod: 'PATCH' // PUT yerine PATCH kullan
                });
                resetForm();
                toggle();
            } catch (error) {
            }
        }
    };

    const getModalTitle = () => {
        return mode === 'add' ? 'Çalışan Ekle' : 'Çalışan Düzenle';
    };

    const getButtonLabel = () => {
        return mode === 'add' ? 'Ekle' : 'Güncelle';
    };

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} backdrop="static">
            <ModalHeader toggle={handleCancel}>{getModalTitle()}</ModalHeader>
            <ModalBody>
                <Form>
                    {mode === 'modify' && (
                        <FormGroup>
                            <Label for="roleId">Rol <span className="text-danger">*</span></Label>
                            <Input
                                type="select"
                                id="roleId"
                                name="roleId"
                                value={employeeData.roleId || ''}
                                onChange={handleRoleChange}
                                invalid={!!errors.roleId}
                            >
                                <option value="">Rol Seçiniz...</option>
                                {roleOptions.map(role => (
                                    <option key={role.id} value={role.id}>
                                        {role.name}
                                    </option>
                                ))}
                            </Input>
                            {errors.roleId && <FormFeedback>{errors.roleId}</FormFeedback>}
                        </FormGroup>
                    )}
                    {mode === 'modify' && (
                        <FormGroup>
                            <Label for="personId">Çalışan ID</Label>
                            <Input
                                type="text"
                                id="personId"
                                name="personId"
                                value={employeeData.personId || ''}
                                placeholder="Çalışan ID (Sistem Tarafından Atanır)"
                                readOnly
                                disabled
                            />
                        </FormGroup>
                    )}
                    <FormGroup>
                        <Label for="firstName">Ad <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={employeeData.firstName || ''}
                            onChange={handleInputChange}
                            placeholder="Ad"
                            invalid={!!errors.firstName}
                        />
                        <FormFeedback>{errors.firstName}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="lastName">Soyad</Label>
                        <Input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={employeeData.lastName || ''}
                            onChange={handleInputChange}
                            placeholder="Soyad"
                            invalid={!!errors.lastName}
                        />
                        <FormFeedback>{errors.lastName}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="tcId">Kimlik Numarası <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="tcId"
                            name="tcId"
                            value={employeeData.tcId || ''}
                            onChange={handleInputChange}
                            placeholder="11 Haneli TC Kimlik No"
                            maxLength={11}
                            invalid={!!errors.tcId}
                        />
                        <FormFeedback>{errors.tcId}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="job">İş / Pozisyon</Label>
                        <Input
                            type="text"
                            id="job"
                            name="job"
                            value={employeeData.job || ''}
                            onChange={handleInputChange}
                            placeholder="Çalışanın Pozisyonu"
                            invalid={!!errors.job}
                        />
                        <FormFeedback>{errors.job}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="department">Departman</Label>
                        <Input
                            type="text"
                            id="department"
                            name="department"
                            value={employeeData.department || ''}
                            onChange={handleInputChange}
                            placeholder="Çalıştığı Departman"
                            invalid={!!errors.department}
                        />
                        <FormFeedback>{errors.department}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="phoneNumber">Telefon Numarası</Label>
                        <Input
                            type="tel"
                            id="phoneNumber"
                            name="phoneNumber"
                            value={employeeData.phoneNumber || ''}
                            onChange={handleInputChange}
                            placeholder="05XX XXX XX XX"
                            maxLength={11}
                            invalid={!!errors.phoneNumber}
                        />
                        <FormFeedback>{errors.phoneNumber}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="email">E-posta Adresi {mode === 'add' && <span className="text-danger">*</span>}</Label>
                        <Input
                            type="email"
                            id="email"
                            name="email"
                            value={employeeData.email || ''}
                            onChange={handleInputChange}
                            placeholder="ornek@sirket.com"
                            invalid={!!errors.email}
                        />
                        <FormFeedback>{errors.email}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="employmentStartDate">İşe Başlama Tarihi</Label>
                        <Input
                            type="date"
                            id="employmentStartDate"
                            name="employmentStartDate"
                            value={employeeData.employmentStartDate || ''}
                            onChange={handleInputChange}
                            invalid={!!errors.employmentStartDate}
                        />
                        <FormFeedback>{errors.employmentStartDate}</FormFeedback>
                    </FormGroup>
                    <FormGroup className="d-flex align-items-center mt-4 mb-3">
                        <Label className="me-3 mb-0" style={{ flexShrink: 0, minWidth: '180px' }}>Hesap Durumu:</Label>
                        <div className="d-flex flex-grow-1">
                            <div className="form-check form-check-inline radio radio-danger me-3">
                                <Input
                                    className="form-check-input"
                                    id="isBlockedTrue"
                                    type="radio"
                                    name="isBlocked"
                                    value="true"
                                    checked={employeeData.isBlocked === true}
                                    onChange={() => handleInputChange({ target: { name: 'isBlocked', value: true, type: 'radio', checked: true } })}
                                />
                                <Label className="form-check-label" htmlFor="isBlockedTrue">Bloklu</Label>
                            </div>
                            <div className="form-check form-check-inline radio radio-success">
                                <Input
                                    className="form-check-input"
                                    id="isBlockedFalse"
                                    type="radio"
                                    name="isBlocked"
                                    value="false"
                                    checked={employeeData.isBlocked === false}
                                    onChange={() => handleInputChange({ target: { name: 'isBlocked', value: false, type: 'radio', checked: true } })}
                                />
                                <Label className="form-check-label" htmlFor="isBlockedFalse">Aktif</Label>
                            </div>
                            {errors.isBlocked && <FormFeedback className="d-block">{errors.isBlocked}</FormFeedback>}
                        </div>
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={handleOperation}>
                    {getButtonLabel()}
                </Button>
                <Button color="secondary" onClick={handleCancel} className="ms-2">
                    İptal
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default EmployeeDataModal;
